import {type ReactNode, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Skeleton} from '@/components/ui/skeleton'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Tag} from '@/components/ui/tag'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FiltrosDeEstado} from '@/components/ui/filtros-de-estado'
import {NoAccessState} from '@/components/no-access-state'
import {PageHeading} from '@/components/page-heading'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {CondicionDialog, PerfilDialog, TerminoDialog} from '@/components/admin-catalogo/dialogos'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  CATALOGO_PAGE_SIZE,
  useAccionCatalogo,
  useBorrarCatalogo,
  useCatalogoCondiciones,
  useCatalogoPerfiles,
  useCatalogoTerminos,
  useCatalogoVacios,
  useCatalogoVersion,
  type CatalogoCondicion,
  type CatalogoEstado,
  type CatalogoPerfil,
  type CatalogoTermino,
  type Familia,
} from '@/hooks/inci/catalogo/use-admin-catalogo'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** «Eliminar» intenta el borrado físico; con citas el backend responde 409 y la salida es archivar.
 * Al firmar, nombre y fecha los pone el backend (usuario autenticado y `bogota_date()`). */

const ESTADOS: readonly CatalogoEstado[] = ['vigentes', 'archivadas', 'todas']

type Pestana = Familia

/** Una fila archivada no desaparece de «todas»: dejó de proponerse, pero lo confirmado la sigue citando. */
type FilaCatalogo = {id: string; clave: string; version: number; vigente: boolean; creado_por_nombre: string | null}

function columnasComunes<T extends FilaCatalogo>(t: (k: string) => string): Column<T>[] {
  return [
    {
      key: 'clave',
      header: t('adminCatalogo.tabla.clave'),
      width: 'w-56',
      cell: (fila) => <span className="font-mono text-xs text-foreground">{fila.clave}</span>,
    },
    {
      key: 'version',
      header: t('adminCatalogo.tabla.version'),
      width: 'w-20',
      cell: (fila) => <span className="font-mono text-xs text-muted-foreground">v{fila.version}</span>,
    },
    {
      key: 'estado',
      header: t('adminCatalogo.tabla.estado'),
      width: 'w-32',
      cell: (fila) => (
        <CeldaEstado
          chip={
            <Tag tone={fila.vigente ? 'green' : 'neutral'} data-testid={`catalogo-estado-${fila.clave}`}>
              {t(fila.vigente ? 'adminCatalogo.estado.vigente' : 'adminCatalogo.estado.archivada')}
            </Tag>
          }
        />
      ),
    },
  ]
}

/** «Editar» va gateado por `vigente`: el backend solo edita la fila vigente (`_para_editar`). */
function Kebab({
  clave,
  vigente,
  onEditar,
  onArchivar,
  onRestaurar,
  onFirmar,
  onEliminar,
}: {
  clave: string
  vigente: boolean
  onEditar: () => void
  onArchivar: () => void
  onRestaurar: () => void
  onFirmar?: () => void
  onEliminar: () => void
}) {
  const {t} = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`catalogo-kebab-${clave}`}
          aria-label={t('adminCatalogo.acciones.menu')}>
          ⋯
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {vigente && (
          <DropdownMenuItem data-testid={`catalogo-editar-${clave}`} onSelect={onEditar}>
            {t('adminCatalogo.acciones.editar')}
          </DropdownMenuItem>
        )}
        {onFirmar && vigente && (
          <DropdownMenuItem data-testid={`catalogo-firmar-${clave}`} onSelect={onFirmar}>
            {t('adminCatalogo.acciones.firmar')}
          </DropdownMenuItem>
        )}
        {vigente ? (
          <DropdownMenuItem data-testid={`catalogo-archivar-${clave}`} onSelect={onArchivar}>
            {t('adminCatalogo.acciones.archivar')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem data-testid={`catalogo-restaurar-${clave}`} onSelect={onRestaurar}>
            {t('adminCatalogo.acciones.restaurar')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`catalogo-eliminar-${clave}`} onSelect={onEliminar}>
          {t('adminCatalogo.acciones.eliminar')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type Objetivo = {familia: Familia; clave: string; version: number}

function PanelPestana({
  testId,
  cargando,
  vacio,
  total,
  page,
  onPageChange,
  buscador,
  children,
}: {
  testId: string
  cargando: boolean
  vacio: boolean
  total: number
  page: number
  onPageChange: (p: number) => void
  buscador: ReactNode
  children: ReactNode
}) {
  const {t} = useTranslation()
  if (cargando) return <Skeleton className="h-64 w-full" />
  return (
    <ListCard
      testId={testId}
      toolbar={buscador}
      footer={
        total > 0 && (
          <PaginationBar page={page} pageSize={CATALOGO_PAGE_SIZE} total={total} onPageChange={onPageChange} />
        )
      }>
      {vacio ? <ListEmptyState page title={t('adminCatalogo.vacio')} /> : children}
    </ListCard>
  )
}

function Buscador({
  valor,
  onValor,
  estado,
  onEstado,
}: {
  valor: string
  onValor: (v: string) => void
  estado: CatalogoEstado
  onEstado: (e: CatalogoEstado) => void
}) {
  const {t} = useTranslation()
  return (
    <>
      <Input
        maxLength={tope('BUSCA')}
        value={valor}
        onChange={(e) => onValor(e.target.value)}
        placeholder={t('adminCatalogo.buscar')}
        className="max-w-xs"
        data-testid="catalogo-buscar"
      />
      <FiltrosDeEstado
        opciones={ESTADOS.map((opcion) => ({valor: opcion, etiqueta: t(`adminCatalogo.filtro.${opcion}`)}))}
        valor={estado}
        onValor={onEstado}
        testIdPrefijo="catalogo-filtro"
      />
    </>
  )
}

export function AdminCatalogoPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const [pestana, setPestana] = useState<Pestana>('perfiles')
  const [estado, setEstado] = useState<CatalogoEstado>('vigentes')
  const tabla = useTableState('clave')
  const [perfilModo, setPerfilModo] = useState<CatalogoPerfil | 'nuevo' | null>(null)
  const [condicionModo, setCondicionModo] = useState<CatalogoCondicion | 'nuevo' | null>(null)
  const [terminoModo, setTerminoModo] = useState<CatalogoTermino | 'nuevo' | null>(null)
  const [archivar, setArchivar] = useState<Objetivo | null>(null)
  const [restaurar, setRestaurar] = useState<Objetivo | null>(null)
  const [firmar, setFirmar] = useState<(Objetivo & {nombre: string}) | null>(null)
  const [eliminar, setEliminar] = useState<Objetivo | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)

  const params = {page: tabla.page, search: tabla.search, estado}
  const perfiles = useCatalogoPerfiles(params, isSystemUser && pestana === 'perfiles')
  const condiciones = useCatalogoCondiciones(params, isSystemUser && pestana === 'condiciones')
  const terminos = useCatalogoTerminos(params, isSystemUser && pestana === 'terminos')
  const vacios = useCatalogoVacios(isSystemUser)
  const version = useCatalogoVersion(isSystemUser)
  const accionArchivar = useAccionCatalogo('archivar')
  const accionRestaurar = useAccionCatalogo('restaurar')
  const accionFirmar = useAccionCatalogo('firmar')
  const borrar = useBorrarCatalogo()

  if (!isSystemUser) return <NoAccessState />

  function cambiarPestana(siguiente: string) {
    setPestana(siguiente as Pestana)
    tabla.setPage(1)
  }

  // Se cierra también al fallar: el mensaje vive en la página y el diálogo lo tapaba junto con el kebab.
  const cerrarCon = (cerrar: () => void) => ({
    onSuccess: () => {
      setFallo(null)
      cerrar()
    },
    onError: (err: unknown) => {
      setFallo(translateApiError(err))
      cerrar()
    },
  })

  const columnasPerfil: Column<CatalogoPerfil>[] = [
    ...columnasComunes<CatalogoPerfil>(t),
    {
      key: 'cargo',
      header: t('adminCatalogo.tabla.cargo'),
      cell: (fila) => (
        <div className="min-w-0">
          <span className="text-sm text-foreground">{fila.cargo_nombre}</span>
          <span className="block text-xs text-muted-foreground">
            {t('adminCatalogo.tabla.prefijos', {prefijos: fila.ciiu_prefijos.join(', ') || '—'})}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-16',
      className: 'text-right',
      cell: (fila) => (
        <Kebab
          clave={fila.clave}
          vigente={fila.vigente}
          onEditar={() => setPerfilModo(fila)}
          onArchivar={() => setArchivar({familia: 'perfiles', clave: fila.clave, version: fila.version})}
          onRestaurar={() => setRestaurar({familia: 'perfiles', clave: fila.clave, version: fila.version})}
          onEliminar={() => setEliminar({familia: 'perfiles', clave: fila.clave, version: fila.version})}
        />
      ),
    },
  ]

  const columnasCondicion: Column<CatalogoCondicion>[] = [
    ...columnasComunes<CatalogoCondicion>(t),
    {
      key: 'firma',
      header: t('adminCatalogo.tabla.firma'),
      width: 'w-40',
      cell: (fila) => (
        <CeldaEstado
          chip={
            <Tag
              tone={fila.estado_norma === 'verificada' ? 'green' : 'amber'}
              data-testid={`catalogo-firma-${fila.clave}`}>
              {t(
                fila.estado_norma === 'verificada' ? 'adminCatalogo.estado.firmada' : 'adminCatalogo.estado.porFirmar'
              )}
            </Tag>
          }
        />
      ),
    },
    {
      key: 'nombre',
      header: t('adminCatalogo.tabla.nombre'),
      cell: (fila) => (
        <div className="min-w-0">
          <span className="text-sm text-foreground">{fila.nombre}</span>
          <span className="block text-xs text-muted-foreground">
            {fila.verificado_por_nombre && fila.verificado_en
              ? t('adminCatalogo.tabla.firmadaPor', {
                  nombre: fila.verificado_por_nombre,
                  fecha: formatDateOnly(fila.verificado_en),
                })
              : t(`catalogoKairos.estado.${fila.estado_norma}`)}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-16',
      className: 'text-right',
      cell: (fila) => (
        <Kebab
          clave={fila.clave}
          vigente={fila.vigente}
          onEditar={() => setCondicionModo(fila)}
          onArchivar={() => setArchivar({familia: 'condiciones', clave: fila.clave, version: fila.version})}
          onRestaurar={() => setRestaurar({familia: 'condiciones', clave: fila.clave, version: fila.version})}
          onFirmar={() =>
            setFirmar({familia: 'condiciones', clave: fila.clave, version: fila.version, nombre: fila.nombre})
          }
          onEliminar={() => setEliminar({familia: 'condiciones', clave: fila.clave, version: fila.version})}
        />
      ),
    },
  ]

  const columnasTermino: Column<CatalogoTermino>[] = [
    ...columnasComunes<CatalogoTermino>(t),
    {
      key: 'terminos',
      header: t('adminCatalogo.tabla.terminos'),
      cell: (fila) => (
        <div className="min-w-0">
          <span className="text-sm text-foreground">{fila.terminos.join(' · ')}</span>
          <span className="block text-xs text-muted-foreground">
            {t(`adminCatalogo.familia.${fila.familia}`)}
            {fila.ancla ? ` · ${fila.ancla}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-16',
      className: 'text-right',
      cell: (fila) => (
        <Kebab
          clave={fila.clave}
          vigente={fila.vigente}
          onEditar={() => setTerminoModo(fila)}
          onArchivar={() => setArchivar({familia: 'terminos', clave: fila.clave, version: fila.version})}
          onRestaurar={() => setRestaurar({familia: 'terminos', clave: fila.clave, version: fila.version})}
          onEliminar={() => setEliminar({familia: 'terminos', clave: fila.clave, version: fila.version})}
        />
      ),
    },
  ]

  const huecos = (vacios.data?.actividades.length ?? 0) + (vacios.data?.condiciones.length ?? 0)
  const nuevo = () => {
    if (pestana === 'perfiles') setPerfilModo('nuevo')
    else if (pestana === 'condiciones') setCondicionModo('nuevo')
    else setTerminoModo('nuevo')
  }
  const buscador = (
    <Buscador
      valor={tabla.searchInput}
      onValor={tabla.setSearchInput}
      estado={estado}
      onEstado={(e) => {
        setEstado(e)
        tabla.setPage(1)
      }}
    />
  )

  return (
    <div className="space-y-5" data-testid="admin-catalogo">
      <PageHeading
        title={t('adminCatalogo.title')}
        subtitle={
          version.data?.fecha
            ? t('adminCatalogo.version', {fecha: formatDateOnly(version.data.fecha)})
            : t('adminCatalogo.sinVersion')
        }
        accion={
          <Button size="sm" data-testid="catalogo-nuevo" onClick={nuevo}>
            {t('adminCatalogo.nuevo')}
          </Button>
        }
      />

      {huecos > 0 && (
        <div className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="catalogo-vacios">
          {t('adminCatalogo.vacios', {
            actividades: vacios.data?.actividades.length ?? 0,
            condiciones: vacios.data?.condiciones.length ?? 0,
          })}{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            data-testid="catalogo-vacios-enlace"
            onClick={() => cambiarPestana((vacios.data?.actividades.length ?? 0) > 0 ? 'perfiles' : 'condiciones')}>
            {t('adminCatalogo.vaciosEnlace')}
          </button>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(vacios.data?.actividades ?? []).map((actividad) => (
              <Tag key={actividad.ciiu_code} tone="neutral" data-testid={`catalogo-vacio-${actividad.ciiu_code}`}>
                {t('adminCatalogo.vacioActividad', {ciiu: actividad.ciiu_code, cargos: actividad.cargos})}
              </Tag>
            ))}
            {(vacios.data?.condiciones ?? []).map((codigo) => (
              <Tag key={codigo} tone="neutral" data-testid={`catalogo-vacio-${codigo}`}>
                {t(`condicionCargo.codigo.${codigo}`, {
                  defaultValue: t(`adminCatalogo.designacion.${codigo}`, {defaultValue: codigo}),
                })}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {fallo && (
        <p className="text-sm text-destructive" data-testid="catalogo-fallo">
          {fallo}
        </p>
      )}

      <Tabs value={pestana} onValueChange={cambiarPestana}>
        <TabsList>
          <TabsTrigger value="perfiles" data-testid="catalogo-tab-perfiles">
            {t('adminCatalogo.tab.perfiles')}
          </TabsTrigger>
          <TabsTrigger value="condiciones" data-testid="catalogo-tab-condiciones">
            {t('adminCatalogo.tab.condiciones')}
          </TabsTrigger>
          <TabsTrigger value="terminos" data-testid="catalogo-tab-terminos">
            {t('adminCatalogo.tab.terminos')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="perfiles" className="mt-4">
          <PanelPestana
            testId="catalogo-tabla-perfiles"
            cargando={perfiles.isLoading && !perfiles.data}
            vacio={perfiles.data?.items.length === 0}
            total={perfiles.data?.total ?? 0}
            page={tabla.page}
            onPageChange={tabla.setPage}
            buscador={buscador}>
            <DataTable columns={columnasPerfil} rows={perfiles.data?.items ?? []} rowKey={(fila) => fila.id} />
          </PanelPestana>
        </TabsContent>

        <TabsContent value="condiciones" className="mt-4">
          <PanelPestana
            testId="catalogo-tabla-condiciones"
            cargando={condiciones.isLoading && !condiciones.data}
            vacio={condiciones.data?.items.length === 0}
            total={condiciones.data?.total ?? 0}
            page={tabla.page}
            onPageChange={tabla.setPage}
            buscador={buscador}>
            <DataTable columns={columnasCondicion} rows={condiciones.data?.items ?? []} rowKey={(fila) => fila.id} />
          </PanelPestana>
        </TabsContent>

        <TabsContent value="terminos" className="mt-4">
          <PanelPestana
            testId="catalogo-tabla-terminos"
            cargando={terminos.isLoading && !terminos.data}
            vacio={terminos.data?.items.length === 0}
            total={terminos.data?.total ?? 0}
            page={tabla.page}
            onPageChange={tabla.setPage}
            buscador={buscador}>
            <DataTable columns={columnasTermino} rows={terminos.data?.items ?? []} rowKey={(fila) => fila.id} />
          </PanelPestana>
        </TabsContent>
      </Tabs>

      <PerfilDialog modo={perfilModo} onCerrar={() => setPerfilModo(null)} />
      <CondicionDialog modo={condicionModo} onCerrar={() => setCondicionModo(null)} />
      <TerminoDialog modo={terminoModo} onCerrar={() => setTerminoModo(null)} />

      <ConfirmDialog
        open={archivar !== null}
        onOpenChange={(abierto) => !abierto && setArchivar(null)}
        title={t('adminCatalogo.archivar.title')}
        description={t('adminCatalogo.archivar.body')}
        confirmLabel={t('adminCatalogo.acciones.archivar')}
        cancelLabel={t('common.cancel')}
        pending={accionArchivar.isPending}
        onConfirm={() =>
          archivar &&
          accionArchivar.mutate(
            {familia: archivar.familia, clave: archivar.clave, version_base: archivar.version},
            cerrarCon(() => setArchivar(null))
          )
        }
      />

      <ConfirmDialog
        open={restaurar !== null}
        onOpenChange={(abierto) => !abierto && setRestaurar(null)}
        title={t('adminCatalogo.restaurar.title')}
        description={t('adminCatalogo.restaurar.body')}
        confirmLabel={t('adminCatalogo.acciones.restaurar')}
        cancelLabel={t('common.cancel')}
        pending={accionRestaurar.isPending}
        onConfirm={() =>
          restaurar &&
          accionRestaurar.mutate(
            {familia: restaurar.familia, clave: restaurar.clave, version_base: restaurar.version},
            cerrarCon(() => setRestaurar(null))
          )
        }
      />

      <ConfirmDialog
        open={firmar !== null}
        onOpenChange={(abierto) => !abierto && setFirmar(null)}
        title={t('adminCatalogo.firmar.title')}
        description={t('adminCatalogo.firmar.body', {nombre: firmar?.nombre ?? ''})}
        confirmLabel={t('adminCatalogo.acciones.firmar')}
        cancelLabel={t('common.cancel')}
        pending={accionFirmar.isPending}
        onConfirm={() =>
          firmar &&
          accionFirmar.mutate(
            {familia: firmar.familia, clave: firmar.clave, version_base: firmar.version},
            cerrarCon(() => setFirmar(null))
          )
        }
      />

      <ConfirmDialog
        open={eliminar !== null}
        onOpenChange={(abierto) => !abierto && setEliminar(null)}
        title={t('adminCatalogo.eliminar.title')}
        description={t('adminCatalogo.eliminar.body')}
        confirmLabel={t('adminCatalogo.acciones.eliminar')}
        cancelLabel={t('common.cancel')}
        pending={borrar.isPending}
        onConfirm={() =>
          eliminar &&
          borrar.mutate(
            {familia: eliminar.familia, clave: eliminar.clave},
            cerrarCon(() => setEliminar(null))
          )
        }
      />
    </div>
  )
}
